import * as THREE from "three";
import type { LoadedModel } from "./loader";
import { registerMesh } from "./materials";

const PRIMARY = new THREE.Color("#F39C7B");
const PRIMARY_DEEP = new THREE.Color("#c63a00");

// Keep the lift small so the mesh doesn't run away from the cursor on click.
// The fade + emissive glow do most of the visual work; lift just hints at
// the focused part being foreground. Larger lift only kicks in once the
// part is *selected* (sticky), at which point the user already committed.
const HOVER_LIFT = 0.05; // fraction of sceneRadius — gentle pop
const SELECTED_LIFT = 0.16; // bigger pop when sticky
const FADE_OPACITY = 0.16;

/**
 * Per-mesh animation state for hover-isolate. We tween:
 *  - position offset along the explosion vector (lift)
 *  - material opacity
 *  - emissive intensity (highlight glow)
 */
type MeshAnim = {
  baseLocalPos: THREE.Vector3;     // local position when assembled, no explosion
  explodeOffset: THREE.Vector3;    // current explosion offset (world units, parent-local)
  hoverOffset: THREE.Vector3;      // current hover lift (parent-local)
  liftDir: THREE.Vector3;          // unit vector in parent-local space
  hoverLiftLen: number;            // peak lift distance while hovered (small)
  selectLiftLen: number;           // peak lift distance while selected (larger)
  targetLift: number;              // 0..1 (lift progress)
  curLift: number;                 // 0..1
  targetOpacity: number;
  curOpacity: number;
  targetEmissive: number;          // 0..1
  curEmissive: number;
};

export class HoverIsolate {
  private animState = new Map<THREE.Mesh, MeshAnim>();
  private partToMeshes = new Map<string, THREE.Mesh[]>();
  private hoveredPart: string | null = null;
  private selectedPart: string | null = null;

  constructor(model: LoadedModel) {
    for (const [meshName, entry] of model.parts) {
      this.partToMeshes.set(meshName, entry.meshes);
      for (const mesh of entry.meshes) {
        registerMesh(mesh);
        const baseLocal = mesh.position.clone();
        // Compute lift direction: from scene center → mesh center, in mesh-parent local space.
        const meshWorld = new THREE.Vector3();
        mesh.getWorldPosition(meshWorld);
        const dirWorld = meshWorld.clone().sub(model.sceneCenter);
        if (dirWorld.lengthSq() < 1e-6) dirWorld.set(0, 1, 0);
        dirWorld.normalize();
        // Convert to local-space direction (rotation only — ignore translation).
        const parent = mesh.parent ?? model.root;
        const parentInvQuat = parent.getWorldQuaternion(new THREE.Quaternion()).invert();
        const dirLocal = dirWorld.clone().applyQuaternion(parentInvQuat).normalize();

        this.animState.set(mesh, {
          baseLocalPos: baseLocal,
          explodeOffset: new THREE.Vector3(),
          hoverOffset: new THREE.Vector3(),
          liftDir: dirLocal,
          hoverLiftLen: HOVER_LIFT * model.sceneRadius,
          selectLiftLen: SELECTED_LIFT * model.sceneRadius,
          targetLift: 0,
          curLift: 0,
          targetOpacity: 1,
          curOpacity: 1,
          targetEmissive: 0,
          curEmissive: 0,
        });
      }
    }
  }

  /** Set hovered part (may be null). Selected part takes precedence visually. */
  setHovered(meshName: string | null) {
    this.hoveredPart = meshName;
    this.applyTargets();
  }

  setSelected(meshName: string | null) {
    this.selectedPart = meshName;
    this.applyTargets();
  }

  /** Update explosion progress for every mesh (0..1). Called by ExplodeToggle. */
  setExplodeOffset(offsets: Map<THREE.Mesh, THREE.Vector3>) {
    for (const [mesh, off] of offsets) {
      const a = this.animState.get(mesh);
      if (!a) continue;
      a.explodeOffset.copy(off);
    }
  }

  /** Per-frame integration. Called from the render loop. */
  tick(dt: number) {
    // Easing: an exponential approach. Half-life 80ms feels snappy but smooth.
    const halfLife = 0.080;
    const k = 1 - Math.pow(2, -dt / halfLife);

    for (const [mesh, a] of this.animState) {
      a.curLift += (a.targetLift - a.curLift) * k;
      a.curOpacity += (a.targetOpacity - a.curOpacity) * k;
      a.curEmissive += (a.targetEmissive - a.curEmissive) * k;

      // Choose hover vs selected lift magnitude based on current focus.
      const part = this.partOf(mesh);
      const isSelected = part === this.selectedPart;
      const isHovered = part === this.hoveredPart;
      // Selected has priority. If hovered (but not selected), small lift.
      const liftLen =
        isSelected ? a.selectLiftLen : isHovered ? a.hoverLiftLen : a.hoverLiftLen;
      a.hoverOffset.copy(a.liftDir).multiplyScalar(a.curLift * liftLen);

      mesh.position.copy(a.baseLocalPos).add(a.explodeOffset).add(a.hoverOffset);

      const mat = (mesh.material as THREE.MeshStandardMaterial) ?? null;
      if (mat) {
        mat.opacity = a.curOpacity;
        mat.transparent = a.curOpacity < 0.999;
        mat.emissive.copy(this.selectedPart && this.partOf(mesh) === this.selectedPart ? PRIMARY_DEEP : PRIMARY);
        mat.emissiveIntensity = a.curEmissive;
        mat.needsUpdate = false; // standard params; no shader recompile
      }
    }
  }

  private partOf(mesh: THREE.Mesh): string | null {
    for (const [name, list] of this.partToMeshes) {
      if (list.includes(mesh)) return name;
    }
    return null;
  }

  private applyTargets() {
    const focusPart = this.hoveredPart ?? this.selectedPart;
    const isSelectedFocus = focusPart && focusPart === this.selectedPart;

    for (const [mesh, a] of this.animState) {
      const part = this.partOf(mesh);
      const isFocus = part === focusPart;
      const isSelected = part === this.selectedPart;

      // Lift: focused part gets full lift; everything else stays put.
      a.targetLift = isFocus ? 1 : 0;

      // Opacity: when something is focused, fade non-focus meshes; otherwise normal.
      if (focusPart) {
        a.targetOpacity = isFocus ? 1.0 : FADE_OPACITY;
      } else {
        a.targetOpacity = 1.0;
      }

      // Emissive glow for focused or selected.
      if (isFocus && isSelectedFocus) a.targetEmissive = 0.55;
      else if (isFocus) a.targetEmissive = 0.32;
      else if (isSelected) a.targetEmissive = 0.20;
      else a.targetEmissive = 0;
    }
  }
}
